import head from 'lodash/head';
import {
  create,
} from 'zustand';
import {
  ServiceFacilityDTO,
  UserDTO,
} from 'dtos';

type ObjectWithId = Record<string, any> & {
  __id: string | number;
};

export type SelectedAppointment = {
  encounterId: number;
  reason?: string;
  doctor?: UserDTO;
  serviceFacility?: ServiceFacilityDTO;
};

const ENTITY_TYPES = ['patient', 'appointment'] as const;
type EntityType = typeof ENTITY_TYPES[number];

type Entities = Record<EntityType, ObjectWithId[]>;

// add to type Entities and defaultEntities when you add a new entity type
const defaultEntities: Entities = Object.freeze({
  patient: [],
  appointment: [],
});

type Props = Entities & {
  actions: {
    setEntity: (entity: object, id: string | number, type?: EntityType) => void;
    unsetEntity: (id: string | number, type?: EntityType) => void;
  };
};

const useSelectedEntitiesStore = create<Props>(
  (set) => ({
    ...defaultEntities,
    actions: {
      setEntity: (entity: object, id: string | number, type: EntityType = 'patient') => set(
        (state: Props) => ({
          ...state,
          [type]: [...state[type], { ...entity, __id: id }],
        }),
      ),
      unsetEntity: (id: string | number, type: EntityType = 'patient') => set(
        (state: Props) => ({
          ...state,
          [type]: state[type].filter((entity) => entity.__id !== id),
        }),
      ),
    },
  }),
);

export function getDefaultPatient() {
  return head(useSelectedEntitiesStore.getState().patient);
}

export function getDefaultAppointment() {
  return head(useSelectedEntitiesStore.getState().appointment) as SelectedAppointment | undefined;
}

export const useDefaultPatient = () => useSelectedEntitiesStore(
  (state: Props) => head(state.patient),
);

export const useDefaultAppointment = () => useSelectedEntitiesStore(
  (state: Props) => head(state.appointment) as SelectedAppointment | undefined,
);

export const useSelectedEntitiesActions = () => useSelectedEntitiesStore(
  (state: Props) => state.actions,
);
